import { useRef, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "app/lib/store/store";
import toast from "react-hot-toast";
import {
  nextStep,
  prevStep,
  addStepsData,
} from "app/lib/store/slices/steps/jointAssetsLiabilities";
import { useAddAssetsLiabilitiesImages } from "app/hooks/assetsLiabilities/useAddAssetsLiabilitiesImages";
import MainTitle from "app/components/common/typography/MainTitle";
import SubTitle from "app/components/common/typography/SubTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import ProgressBar from "app/components/common/progressbar/ProgressBar";
import InfoTitle from "app/components/common/typography/InfoTitle";
import Loader from "app/components/common/Loader";
import DriverLicenseUpload from "app/components/imagesUploads/joint/DriverLicenseUpload";
import { useS3Upload } from "app/hooks/assetsLiabilities/useS3Upload";
import { getPresignedUrlsPromise } from "app/utils/getPresignedUrlsPromise";

interface FormData {
  frontImage: File | null;
  backImage: File | null;
  cofrontImage: File | null;
  cobackImage: File | null;
}

const PartSix: React.FC = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const { mutate: uploadS3 } = useS3Upload();
  const { mutateAsync: uploadImages } = useAddAssetsLiabilitiesImages();
  const { driverlicenseimages, ...others } = useSelector(
    (state: RootState) => state.jointAssetsLiabilities.stepsData
  );
  const savedIds = useSelector(
    (state: RootState) => state.loanTypesSteps?.savedIds
  );

  const [yourFrontPreview, setYourFrontPreview] = useState<string | null>(null);
  const [yourBackPreview, setYourBackPreview] = useState<string | null>(null);
  const [coFrontPreview, setCoFrontPreview] = useState<string | null>(null);
  const [coBackPreview, setCoBackPreview] = useState<string | null>(null);

  const frontFileInputRef = useRef<HTMLInputElement>(null);
  const backFileInputRef = useRef<HTMLInputElement>(null);
  const cofrontFileInputRef = useRef<HTMLInputElement>(null);
  const cobackFileInputRef = useRef<HTMLInputElement>(null);
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    clearErrors,
    setError,
  } = useForm<FormData>();

  const frontImage = watch("frontImage");
  const backImage = watch("backImage");
  const cofrontImage = watch("cofrontImage");
  const cobackImage = watch("cobackImage");

  const handleFileChooseClick = (type: "front" | "back") => {
    if (type === "front" && frontFileInputRef.current) {
      frontFileInputRef.current.click();
    } else if (type === "back" && backFileInputRef.current) {
      backFileInputRef.current.click();
    }
  };
  const cohandleFileChooseClick = (type: "front" | "back") => {
    if (type === "front" && cofrontFileInputRef.current) {
      cofrontFileInputRef.current.click();
    } else if (type === "back" && cobackFileInputRef.current) {
      cobackFileInputRef.current.click();
    }
  };

  // Handle file selection
  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    fileType: "front" | "back"
  ) => {
    const file = e.target.files?.[0] ?? null;
    const fieldName = fileType === "front" ? "frontImage" : "backImage";

    clearErrors(fieldName);
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setError(fieldName, {
        type: "fileSize",
        message: "File size should not exceed 5MB",
      });
      e.target.value = "";
      return;
    }
    const isValidType = ["image/jpeg", "image/png", "application/pdf"].includes(
      file.type
    );
    if (!isValidType) {
      setError(fieldName, {
        type: "fileType",
        message: "Only JPG, PNG, or PDF files allowed",
      });
      e.target.value = "";
      return;
    }
    setValue(fieldName, file);
    clearErrors(fieldName);
  };

  const cohandleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    fileType: "front" | "back"
  ) => {
    const file = e.target.files?.[0] ?? null;
    const fieldName = fileType === "front" ? "cofrontImage" : "cobackImage";

    clearErrors(fieldName);
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError(fieldName, {
        type: "fileSize",
        message: "File size should not exceed 5MB",
      });
      e.target.value = "";
      return;
    }
    const isValidType = ["image/jpeg", "image/png", "application/pdf"].includes(
      file.type
    );
    if (!isValidType) {
      setError(fieldName, {
        type: "fileType",
        message: "Only JPG, PNG, or PDF files allowed",
      });
      e.target.value = "";
      return;
    }
    setValue(fieldName, file);
    clearErrors(fieldName);
  };

  useEffect(() => {
    const images = {
      frontImage,
      backImage,
      cofrontImage,
      cobackImage,
    };

    Object.entries(images).forEach(([key, file]) => {
      if (file instanceof File) {
        const objectUrl = URL.createObjectURL(file);

        // Set preview dynamically
        switch (key) {
          case "frontImage":
            setYourFrontPreview(objectUrl);
            break;
          case "backImage":
            setYourBackPreview(objectUrl);
            break;
          case "cofrontImage":
            setCoFrontPreview(objectUrl);
            break;
          case "cobackImage":
            setCoBackPreview(objectUrl);
            break;
        }

        // Cleanup function to revoke object URLs
        return () => URL.revokeObjectURL(objectUrl);
      } else {
        // Fallback to existing stored image
        switch (key) {
          case "frontImage":
            setYourFrontPreview(driverlicenseimages?.frontImage || null);
            break;
          case "backImage":
            setYourBackPreview(driverlicenseimages?.backImage || null);
            break;
          case "cofrontImage":
            setCoFrontPreview(driverlicenseimages?.cofrontImage || null);
            break;
          case "cobackImage":
            setCoBackPreview(driverlicenseimages?.cobackImage || null);
            break;
        }
      }
    });
  }, [
    frontImage,
    backImage,
    cofrontImage,
    cobackImage,
    driverlicenseimages?.frontImage,
    driverlicenseimages?.backImage,
    driverlicenseimages?.cofrontImage,
    driverlicenseimages?.cobackImage,
  ]);

  const onSubmit = async (data: FormData) => {
    const { frontImage, backImage, cobackImage, cofrontImage } = data || {};

    const hasAnyImage = [frontImage, backImage, cofrontImage, cobackImage].some(
      (img) => img instanceof File
    );
    if (!hasAnyImage) {
      dispatch(nextStep());
      return;
    }

    const yourUploadData: Record<string, File> = {};
    const coUploadData: Record<string, File> = {};
    const yourDocumentTypes: string[] = [];
    const coDocumentTypes: string[] = [];

    if (frontImage instanceof File) {
      yourDocumentTypes.push("front_driver_license");
      yourUploadData.frontImage = frontImage;
    }
    if (backImage instanceof File) {
      yourUploadData.backImage = backImage;
      yourDocumentTypes.push("back_driver_license");
    }

    if (cofrontImage instanceof File) {
      coDocumentTypes.push("front_driver_license");
      coUploadData.frontImage = cofrontImage;
    }
    if (cobackImage instanceof File) {
      coDocumentTypes.push("back_driver_license");
      coUploadData.backImage = cobackImage;
    }

    if (
      Object.keys(coUploadData).length === 0 &&
      Object.keys(yourUploadData).length === 0
    ) {
      dispatch(nextStep());
      return;
    }
    // Get presigned URLs

    try {
      setIsLoading(true);
      let yourPresignedData: any = null;
      let coPresignedData: any = null;
      const presignedRequests: Promise<any>[] = [];

      if (yourDocumentTypes.length > 0) {
        presignedRequests.push(
          getPresignedUrlsPromise({
            url: `/document/${savedIds.applicantID}/presigned-urls`,
            data: { documentTypes: yourDocumentTypes },
          }).then((res) => {
            yourPresignedData = res;
          })
        );
      }
      if (coDocumentTypes.length > 0) {
        presignedRequests.push(
          getPresignedUrlsPromise({
            url: `/document/${savedIds.secondApplicantID}/presigned-urls`,
            data: { documentTypes: coDocumentTypes },
          }).then((res) => {
            coPresignedData = res;
          })
        );
      }

      await Promise.all(presignedRequests);

      const yourMetadata: Record<string, any> = {};
      const coMetadata: Record<string, any> = {};

      // Upload your driver's license
      if (
        frontImage instanceof File &&
        yourPresignedData?.front_driver_license
      ) {
        const { url, key } = yourPresignedData?.front_driver_license || {};
        await uploadToS3(url, frontImage);
        yourMetadata["frontDriverLicense"] = {
          key,
          fileName: frontImage.name,
          mimeType: frontImage.type,
        };
      }
      if (backImage instanceof File && yourPresignedData?.back_driver_license) {
        const { url, key } = yourPresignedData?.back_driver_license || {};
        await uploadToS3(url, backImage);
        yourMetadata["backDriverLicense"] = {
          key,
          fileName: backImage.name,
          mimeType: backImage.type,
        };
      }

      if (
        cofrontImage instanceof File &&
        coPresignedData?.front_driver_license
      ) {
        const { url, key } = coPresignedData?.front_driver_license || {};
        await uploadToS3(url, cofrontImage);
        coMetadata["frontDriverLicense"] = {
          key,
          fileName: cofrontImage.name,
          mimeType: cofrontImage.type,
        };
      }

      if (cobackImage instanceof File && coPresignedData?.back_driver_license) {
        const { url, key } = coPresignedData?.back_driver_license || {};
        await uploadToS3(url, cobackImage);
        coMetadata["backDriverLicense"] = {
          key,
          fileName: cobackImage.name,
          mimeType: cobackImage.type,
        };
      }
      const uploadRequests: Promise<any>[] = [];
      let hasYourUpload = Object.keys(yourMetadata).length > 0;
      let hasCoUpload = Object.keys(coMetadata).length > 0;

      if (Object.keys(yourMetadata).length > 0) {
        uploadRequests.push(
          uploadImages({
            url: `/document/${savedIds.applicantID}/driver-license`,
            data: yourMetadata,
          })
        );
      }

      if (Object.keys(coMetadata).length > 0) {
        uploadRequests.push(
          uploadImages({
            url: `/document/${savedIds.secondApplicantID}/driver-license`,
            data: coMetadata,
          })
        );
      }

      const uploadResponses = await Promise.all(uploadRequests);
      let yourResponse: any[] = [];
      let coResponse: any[] = [];
      if (hasYourUpload && hasCoUpload) {
        [yourResponse, coResponse] = uploadResponses;
      } else if (hasYourUpload) {
        [yourResponse] = uploadResponses;
      } else if (hasCoUpload) {
        [coResponse] = uploadResponses;
      }

      const uplodedDriverlicenseimages: Record<string, string> = {
        ...(driverlicenseimages || {}), // existing data
      };
      yourResponse?.forEach((item: any) => {
        if (item.type === "front_driver_license") {
          uplodedDriverlicenseimages.frontImage = item.url;
        } else if (item.type === "back_driver_license") {
          uplodedDriverlicenseimages.backImage = item.url;
        }
      });
      // Process co-applicant response
      coResponse?.forEach((item: any) => {
        if (item.type === "front_driver_license") {
          uplodedDriverlicenseimages.cofrontImage = item.url;
        } else if (item.type === "back_driver_license") {
          uplodedDriverlicenseimages.cobackImage = item.url;
        }
      });
      dispatch(
        addStepsData({
          ...others,
          driverlicenseimages: uplodedDriverlicenseimages,
        })
      );

      dispatch(nextStep());
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      toast.error(error?.response?.data?.message || "Something went wrong.");
    }
  };

  const uploadToS3 = (url: string, file: File) => {
    return new Promise<void>((resolve, reject) => {
      uploadS3(
        { url, data: file },
        {
          onSuccess: (status: any) => (status === 200 ? resolve() : reject()),
          onError: reject,
        }
      );
    });
  };
  const handleDeleteImage = (
    type: "frontImage" | "backImage" | "cofrontImage" | "cobackImage"
  ) => {
    const previewSetters = {
      frontImage: setYourFrontPreview,
      backImage: setYourBackPreview,
      cofrontImage: setCoFrontPreview,
      cobackImage: setCoBackPreview,
    };
    const updatePreview = previewSetters[type];
    if (updatePreview) {
      dispatch(
        addStepsData({
          ...others,
          driverlicenseimages: {
            ...driverlicenseimages,
            [type]: "",
          },
        })
      );
      updatePreview(null);
      setValue(type, null);
    }
  };

  return (
    <>
      {isLoading && (
        <Loader text="Please wait while your documents are being uploaded..." />
      )}
      <ProgressBar progress={84} MB={4} />
      <div className="w-full sm:w-[60%] lg:w-[50%] mx-auto text-center px-4 sm:px-0">
        <MainTitle title="Legal Documents" />
        <SubTitle title="Please upload passport or driver license images" />
      </div>
      <div className="mx-auto w-internalWidth">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex mb-8 flex-col md:flex-col xl:flex-row gap-8 lg:gap-7 xl:gap-4 justify-between">
            <div className="w-full md:w-full lg:w-w-full xl:w-[49%]">
              <InfoTitle title="Your Details" />
              <div className="text-left">
                <h6 className="font-Mulish text-nowrap text-base text-lightBlue mb-2">
                  Upload driver license images
                  <span className="text-danger">*</span>
                </h6>
                <div className="flex gap-7 sm:gap-4  flex-col lg:flex-row justify-between">
                  <DriverLicenseUpload
                    label="Front side of your document"
                    previewUrl={yourFrontPreview}
                    fieldName="frontImage"
                    savedFileUrl={driverlicenseimages?.frontImage}
                    register={register}
                    error={errors.frontImage}
                    fileInputRef={frontFileInputRef}
                    onFileChange={(e) => handleFileChange(e, "front")}
                    onChooseFile={() => handleFileChooseClick("front")}
                    onDelete={() => handleDeleteImage("frontImage")}
                    imageObject={frontImage}
                  />

                  <DriverLicenseUpload
                    label="Back side of your document"
                    previewUrl={yourBackPreview}
                    fieldName="backImage"
                    savedFileUrl={driverlicenseimages?.backImage}
                    register={register}
                    error={errors.backImage}
                    fileInputRef={backFileInputRef}
                    onFileChange={(e) => handleFileChange(e, "back")}
                    onChooseFile={() => handleFileChooseClick("back")}
                    onDelete={() => handleDeleteImage("backImage")}
                    imageObject={backImage}
                  />
                </div>
              </div>
            </div>
            <div className="w-full md:w-full lg:w-w-full xl:w-[49%] justify-between">
              <InfoTitle title="Co-borrower" />
              <div className="text-left">
                <h6 className="font-Mulish text-nowrap text-base text-lightBlue mb-2">
                  Upload driver license images
                  <span className="text-danger">*</span>
                </h6>
                <div className="flex gap-7 sm:gap-4  flex-col lg:flex-row justify-between ">
                  <DriverLicenseUpload
                    label="Front side of your document"
                    previewUrl={coFrontPreview}
                    fieldName="cofrontImage"
                    savedFileUrl={driverlicenseimages?.cofrontImage}
                    register={register}
                    error={errors.cofrontImage}
                    fileInputRef={cofrontFileInputRef}
                    onFileChange={(e) => cohandleFileChange(e, "front")}
                    onChooseFile={() => cohandleFileChooseClick("front")}
                    onDelete={() => handleDeleteImage("cofrontImage")}
                    imageObject={cofrontImage}
                  />
                  <DriverLicenseUpload
                    label="Back side of your document"
                    previewUrl={coBackPreview}
                    fieldName="cobackImage"
                    savedFileUrl={driverlicenseimages?.cobackImage}
                    register={register}
                    error={errors.cobackImage}
                    fileInputRef={cobackFileInputRef}
                    onFileChange={(e) => cohandleFileChange(e, "back")}
                    onChooseFile={() => cohandleFileChooseClick("back")}
                    onDelete={() => handleDeleteImage("cobackImage")}
                    imageObject={cobackImage}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-between mb-6">
            <PreviousButtom
              onClick={() => {
                dispatch(prevStep());
              }}
            >
              Previous
            </PreviousButtom>
            <ContinueButton type="submit" loading={isLoading}>
              Next
            </ContinueButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default PartSix;
